import { useEffect, useState } from 'react';
import { fetchAuthBlob } from '@utils/blobUrl';
import { isTrustedApiUrl } from '@utils/apiOrigin';
import type { AttachmentSource } from './types';

export interface AttachmentBlobState {
  /** Object URL for `<img>`/anchor consumers. Revoked automatically. */
  url: string | null;
  isLoading: boolean;
  hasError: boolean;
}

/** Identity of the source a resolution belongs to, so stale results are ignored. */
type SourceKey = File | string;

interface Resolution {
  key: SourceKey;
  /** `null` once the source turned out to be unreadable. */
  url: string | null;
}

const loadBlob = async (file: File | null, remoteUrl: string | null): Promise<Blob> => {
  if (file) return file;
  // Untrusted origins are refused outright: fetching one would hand the
  // Bearer token to a third-party host.
  if (remoteUrl && isTrustedApiUrl(remoteUrl)) return fetchAuthBlob(remoteUrl);
  throw new Error('Attachment source is not readable');
};

/**
 * Resolve an attachment source to bytes, gated by `enabled` so nothing is
 * fetched for a tile that is off-screen, too large to preview, or not
 * previewable at all. Local files never touch the network.
 */
export const useAttachmentBlobUrl = (
  source: AttachmentSource | null,
  enabled: boolean,
): AttachmentBlobState => {
  const [resolution, setResolution] = useState<Resolution | null>(null);

  // Depend on the underlying File / URL rather than the wrapper object, which
  // callers rebuild on every render.
  const file = source?.kind === 'local' ? source.file : null;
  const remoteUrl = source?.kind === 'remote' ? source.url : null;
  const key: SourceKey | null = file ?? remoteUrl;
  const active = enabled && key !== null;

  useEffect(() => {
    if (!active || key === null) return;

    let cancelled = false;
    let objectUrl: string | null = null;

    void loadBlob(file, remoteUrl)
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setResolution({ key, url: objectUrl });
      })
      .catch(() => {
        if (!cancelled) setResolution({ key, url: null });
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [active, file, remoteUrl, key]);

  const current = resolution?.key === key ? resolution : null;

  return {
    url: current?.url ?? null,
    isLoading: active && current === null,
    hasError: current !== null && current.url === null,
  };
};
